import React from 'react';

import type { PortableTextComponents } from '@portabletext/react';
import { PortableText } from '@portabletext/react';
import { Typography, TypographyVariant } from '../atoms/typography';
import { SvgArrowRight } from '../../../icons';

import { Link } from 'gatsby';
import { formatLink } from '../../../helpers/format-link';
import { SanityLink } from '../../../../graphql-types';
import Button from '../atoms/button';
import { LoginOrRegisterButton, SubmitIdeaButton } from '../../richTextSerializer';
import { useLanguage } from '../../../i18n';
import clsx from 'clsx';

const LinkWithLanguageProvider = ({
    value,
    children,
}: {
    value: SanityLink;
    children: React.ReactNode;
}) => {
    const language = useLanguage();

    const formattedLink = formatLink({ ...value, language } as SanityLink);

    if (!formattedLink) {
        return null;
    }

    const { path, isExternal, newTab } = formattedLink;

    if (!path) {
        return null;
    }

    return isExternal ? (
        <a
            target={'_blank'}
            rel="noopener"
            href={path}
            className="underline hover:no-underline duration-300  focus-visible:focus-style-dark inline-flex items-center gap-x-1"
        >
            {children} {newTab ? <SvgArrowRight className="size-6" /> : null}
        </a>
    ) : (
        <Link
            to={path}
            className="underline hover:no-underline duration-300 focus-visible:focus-style-dark"
        >
            {' '}
            {children}
        </Link>
    );
};

const simplePortableTextComponents: PortableTextComponents = {
    block: {
        normal: ({ children, value, index }) => {
            if (value.children && value.children[0] && value.children[0].text === '' && index > 0) {
                return <br />;
            }
            return <p className="mb-2">{children}</p>;
        },

        h3: ({ children }) => {
            return (
                <Typography as="h3" variant="hd-lg" desktopVariant="hd-xl" className="mb-2">
                    {children}
                </Typography>
            );
        },
        h4: ({ children }) => {
            return (
                <Typography as="h4" variant="hd-md" desktopVariant="hd-lg" className="mb-2">
                    {children}
                </Typography>
            );
        },
    },
    marks: {
        link: ({ children = [], value }) => {
            if (!value) {
                return null;
            }

            return <LinkWithLanguageProvider value={value}>{children}</LinkWithLanguageProvider>;
        },
    },
    list: {
        bullet: ({ children }) => <ul className="mb-2 ml-8 list-disc ">{children}</ul>,
        number: ({ children }) => <ol className="mb-2 ml-8 list-decimal">{children}</ol>,
    },
    listItem: {
        bullet: ({ children }) => <li className="text-p-reg-md">{children}</li>,
        number: ({ children }) => <li className="text-p-reg-md">{children}</li>,
    },
    // types: {
    //     // Need to replace with correct design
    //     button: ({ value }) => {
    //         const align = value.align || 'center';
    //         const displayOptions = {
    //             // Inside other boxes the sizes should be adjusted based on the outer box
    //             maxWidth: '100%',
    //             maxWidthProductSelection: '100%',
    //         };
    //         if (value.buttonType === 'link') {
    //             return (
    //                 <div>
    //                     <div
    //                         className={clsx('flex', {
    //                             'justify-start': align === 'left',
    //                             'justify-center': align === 'center',
    //                             'justify-end': align === 'right',
    //                         })}
    //                     >
    //                         <Button as="a" href={value.url}>
    //                             {value.text}
    //                         </Button>
    //                     </div>
    //                 </div>
    //             );
    //         }
    //         if (value.buttonType === 'login' || value.buttonType === 'register') {
    //             return (
    //                 <LoginOrRegisterButton
    //                     displayOptions={displayOptions}
    //                     type={value.buttonType}
    //                     align={align}
    //                 >
    //                     {value.text}
    //                 </LoginOrRegisterButton>
    //             );
    //         }
    //         if (value.buttonType === 'submitIdea') {
    //             return (
    //                 <SubmitIdeaButton displayOptions={displayOptions} align={align}>
    //                     {value.text}
    //                 </SubmitIdeaButton>
    //             );
    //         }

    //         return null;
    //     },
    // },
};

type RichTextProps = {
    value?: any;
    variant?: TypographyVariant;
    desktopVariant?: TypographyVariant;
    className?: string;
};

export function RichText({
    value,
    variant = 'body-md',
    className,
    desktopVariant = 'body-lg',
}: RichTextProps) {
    if (!value) {
        return null;
    }

    return (
        <Typography
            as="div"
            variant={variant}
            desktopVariant={desktopVariant}
            className={clsx('[&>*:last-child]:mb-0', className)}
        >
            <PortableText value={value} components={simplePortableTextComponents} />
        </Typography>
    );
}
